import {Injectable} from '@angular/core';
import {BaseProvider} from '../base.provider';
import {NotificationsService} from './notifications.service';
import {BehaviorSubject} from 'rxjs';
import {NotificationsInterfaces} from '@namespaces/notifications.namespace';
import {NotificationMessage} from '@providers/notifications/notification-message';
import {EventsService} from '@services/events.service';

@Injectable()
export class NotificationsProvider extends BaseProvider<NotificationMessage[]> {
    public defaultValue: any[] = [];

    /**
     * Количество новых, непрочитанных уведомлений
     */
    public countNew$ = new BehaviorSubject<number>(0);

    /**
     * Накапливаем ids прочитанных уведомлений, чтобы потом по необходимости сделать их прочитанными
     */
    private readedNotiesIds: number[] = [];

    constructor(
        protected service: NotificationsService,
        private events: EventsService,
    ) {
        super(service);
        /**
         * Устанавливаем дефолтные значения
         */
        this.data$.next(this.defaultValue);
        this.updateData();

        /**
         * При разлогировании очищаем все уведомления
         */
        this.events.logout$.subscribe(() => {
            this.clearData();
            this.readedNotiesIds = [];
        }, () => {});
    }

    /**
     * Переопределяем метод обновления уведомлений, так как уведомления будут добавляться в провайдер
     * через метод add
     *
     * @param {NotificationMessage[]} data
     */
    public updateData(data: NotificationMessage[] = []): void {
        try {
            this.data$.next(this.processedData(data));
        } catch (e) {
            this.data$.next(this.defaultValue);
        }
    }

    processedData(data: NotificationMessage[] = []) {
        this.countNew$.next(this.getNewCount(data));
        return data;
    }

    /**
     * Добавить уведомления к уже имеющимся
     * Добавляются только те уведомления, которых еще не было в системе. Проверка идет по id
     * @param data
     */
    public addData(data: NotificationsInterfaces.Message[] = []): void {
        if (!data || !data.length) {
            return;
        }

        /**
         * Берем только те данные, которых еще нет.
         * Это проверка на случай, если вдруг в запросе пришли уже существующие в системе уведомления
         */
        const currentData = this.data$.getValue();
        const currentIds = currentData.map(d => +d.id);
        const newDataToAdd = data.filter(item => !currentIds.includes(+item.id));
        if (!newDataToAdd.length) {
            return;
        }
        /**
         * Перед добавлением преобразуем уведомления в объекты класса со своей внутренней логикой
         */
        const messages = newDataToAdd.map(item => new NotificationMessage(item));
        /**
         * Добавляем новые уведомления в начало списка
         */
        this.updateData(messages.concat(currentData));
    }

    /**
     * Отметить все уведомления как прочитанные
     */
    public realAll() {
        this.service.get({
            url: this.service.readAllUrl
        }).toPromise().then(() => {
            const currentData = this.data$.getValue();
            currentData.forEach(item => item.isRead = true);
            this.updateData(currentData);
        }, () => {
            this.events.notyShow$.next({
                type: 'warning',
                title: 'Не удалось отметить все уведомления как прочитанные',
                content: 'Попробуйте попытку позднее или обратитесь в техническую поддержку'
            });
        });
    }

    /**
     * Прочитать уведомления с указанными ids.
     * Запоминаем id уведомлений, которые нужно отметить как прочитанные.
     * Делать их прочитанными будем только в вызове определенного метода
     * @param ids
     */
    public readNoties(ids: number[] = []) {

        /**
         * Отмечаем прочитанными только те уведомления, которые еще не были прочитаны
         */
        const newIds = ids.filter(id => !this.readedNotiesIds.includes(id));

        if (!newIds.length) {
            return;
        }

        this.service.get({
            url: this.service.readUrl,
            queryParams: {
                ids: newIds,
            }
        }).toPromise().then(() => {
            /**
             * Ничего не делаем, так как отмечать прочитанными будем вызовом другого метода тогда, когда это будет нужно.
             * Только запоминаем ids уведомлений, которые нужно будет прочитать.
             */
            this.readedNotiesIds.push(...newIds);
        }, () => {

        });
    }

    /**
     * Отметить прочитанными уведомления
     */
    public markNotiesAsRead() {
        if (!this.readedNotiesIds.length) {
            return;
        }
        const currentData = this.data$.getValue();
        currentData.filter(item => this.readedNotiesIds.includes(item.id)).forEach(item => item.isRead = true);
        this.updateData(currentData);
        this.readedNotiesIds = [];
    }

    /**
     * Удалить уведомления с определенным id
     * @param id
     */
    public removeNoty(id: number) {
        this.service.delete({
            id,
        }).toPromise().then(() => {
            const currentData = this.data$.getValue();
            this.updateData(currentData.filter(item => +item.id !== +id));
        }, () => {
            this.events.notyShow$.next({
                type: 'warning',
                title: 'Не удалось удалить уведомление',
                content: 'Попробуйте попытку позднее или обратитесь в техническую поддержку'
            });
        });
    }

    /**
     * Удалить все уведомления
     */
    public removeAll() {
        this.service.delete({
            url: this.service.removeAllUrl
        }).toPromise().then(() => {
            this.clearData();
        }, () => {
            this.events.notyShow$.next({
                type: 'warning',
                title: 'Не удалось очистить список уведомлений',
                content: 'Попробуйте попытку позднее или обратитесь в техническую поддержку'
            });
        });
    }

    /**
     * Получить количество новых уведомлений
     * @param {NotificationMessage[]} data
     * @returns {number}
     */
    private getNewCount(data: NotificationMessage[] = []): number {
        return data.filter(item => item.hasOwnProperty('isRead') && !item.isRead).length;
    }
}
